// Core API client utilities (extracted from the monolithic api.ts)
// Exports: buildUrl, apiRequest, ApiError, setTokens, clearTokens, getAccessToken, isAuthenticated

// Automatically use HTTPS if the page is loaded over HTTPS
const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  // Build-time SSR (Node) nie ma `location` — wtedy używamy ścieżek względnych.
  if (typeof globalThis.location === 'undefined') {
    return '';
  }
  const protocol = globalThis.location.protocol; // Already includes ':'
  const hostname = globalThis.location.hostname;

  if (import.meta.env.DEV || hostname === 'localhost' || /^192\.168\./.test(hostname)) {
    const apiUrl = `${protocol}//${hostname}:3001`;
    console.log('API Base URL (dev):', apiUrl);
    return apiUrl;
  }

  console.log('API Base URL (prod): using relative /api paths');
  return '';
};

export const API_BASE_URL = getApiBaseUrl();

// For cookie-based auth we avoid storing tokens in localStorage.
// We keep a small in-memory cache to support legacy flows if backend returns tokens,
// but the primary auth mechanism is httpOnly cookies sent by the server.
let accessToken: string | null = null;

export const setTokens = (access: string) => {
  accessToken = access || null;
};

export const clearTokens = () => {
  accessToken = null;
};

export const getAccessToken = () => accessToken;
export const isAuthenticated = () => !!accessToken; // legacy: prefer server-side check

export class ApiError extends Error {
  status: number;
  response?: unknown;

  constructor(message: string, status: number, response?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.response = response;
  }
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

export interface PaginatedResponse<T> {
  entries: T[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export function buildUrl(endpoint: string): string {
  const base = (API_BASE_URL || '').replace(/\/+$/, '');
  let ep = endpoint;
  if (base.endsWith('/api') && ep.startsWith('/api')) {
    ep = ep.replace(/^\/api/, '');
  }
  if (!ep.startsWith('/')) ep = '/' + ep;
  if (!base) return ep;
  return base + ep;
}

export async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = buildUrl(endpoint);

  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
  };

  // Only set default Content-Type if not already set and body is not FormData
  if (!headers['Content-Type'] && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  // If Content-Type is specifically marked for removal (multipart), delete it
  // to let the browser set the correct boundary.
  if (headers['Content-Type'] === 'MULTIPART_MARKER') {
    delete headers['Content-Type'];
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  try {
    try {
      const safeOptions = { ...options, headers } as unknown;
      console.debug?.('[apiRequest] fetching', url, safeOptions);
    } catch (logErr) {
      // ignore logging errors
      void logErr;
    }

    const response = await (globalThis as unknown as typeof globalThis).fetch(url, {
      ...options,
      headers,
      // Include credentials so httpOnly cookies set by backend are sent with requests
      credentials: 'include',
    });

    if (!response.ok) {
      const skipRefreshFor = [
        '/api/auth/login',
        '/api/auth/register',
        '/api/auth/refresh',
        '/api/auth/logout',
        '/api/auth/me', // don't attempt refresh when checking current session
        '/api/recipes/public', // guest catalog — never bounce to /login
      ];

      if (response.status === 401 && !skipRefreshFor.some((p) => endpoint.startsWith(p))) {
        // Try a cookie-based refresh (backend should read refresh token from httpOnly cookie)
        try {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            // If refresh returned a runtime access token, set Authorization header for retry
            if (getAccessToken()) headers['Authorization'] = `Bearer ${getAccessToken()}`;
            const retryResponse = await fetch(url, { ...options, headers, credentials: 'include' });
            if (retryResponse.ok) {
              const data = await retryResponse.json();
              return data;
            }
          }
        } catch (e) {
          console.warn('Cookie refresh attempt failed', e);
        }

        // Check if redirect should be skipped
        if ((options as any).skipAuthRedirect) {
          throw new ApiError('Authentication failed', 401, { message: 'Authentication failed' });
        }

        console.error('Authentication failed - redirecting to login');
        globalThis.location.href = '/login';
        throw new Error('Session expired. Please login again.');
      }

      const error = await response.json().catch(() => ({ message: response.statusText }));
      // Prefer explicit 'error' field from API, then 'message', then default HTTP text
      const errorMessage =
        (error && (error as { error?: string }).error) ||
        (error && (error as { message?: string }).message) ||
        `HTTP ${response.status}: ${response.statusText}`;

      throw new ApiError(errorMessage, response.status, error);
    }

    const data = await response.json();
    return data;
  } catch (err: unknown) {
    // Check if this is an aborted request
    const isAborted =
      (err instanceof DOMException && err.name === 'AbortError') ||
      (err as Error)?.name === 'AbortError';

    if (isAborted) {
      // Re-throw AbortError without logging
      if (err instanceof Error) throw err;
      throw new Error('AbortError', { cause: err });
    }

    try {
      // Avoid noisy logging for expected 401 responses to /api/auth/me (e.g. post-logout).
      const suppressLoggingFor = ['/api/auth/me'];
      const isApiError = err instanceof ApiError;
      const shouldSuppress =
        isApiError &&
        (err as ApiError).status === 401 &&
        suppressLoggingFor.some((p) => endpoint.startsWith(p));

      if (!shouldSuppress) {
        console.error?.('[apiRequest] fetch/network error', { url, err });
      }
    } catch (logErr) {
      // ignore logging errors
      void logErr;
    }
    if (err instanceof ApiError) throw err;
    // Never expose endpoint URLs in Error.message (shown in UI by many callers).
    // Full URL stays in console.error above for debugging.
    throw new ApiError('Błąd ładowania', 0, {
      originalMessage: err instanceof Error ? err.message : String(err),
      url,
    });
  }
}

export async function refreshAccessToken(): Promise<string | null> {
  try {
    console.log('Attempting cookie-based refresh (no body)');
    const response = await (globalThis as unknown as typeof globalThis).fetch(
      buildUrl('/api/auth/refresh'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // No body - backend should check httpOnly cookie for refresh token
        credentials: 'include',
      },
    );

    if (response.ok) {
      const data = await response.json().catch(() => null);
      // If backend returns runtime tokens for compatibility, cache them in-memory
      const newAccessToken = data?.data?.tokens?.accessToken as string | undefined;
      if (newAccessToken) setTokens(newAccessToken);
      console.log('Cookie refresh successful');
      return newAccessToken || null;
    }
  } catch (error) {
    console.error('Token refresh error:', error);
  }

  clearTokens();
  return null;
}

export default {
  buildUrl,
  apiRequest,
  ApiError,
  setTokens,
  clearTokens,
  getAccessToken,
  isAuthenticated,
  refreshAccessToken,
  get: <T>(endpoint: string, options?: RequestInit) =>
    apiRequest<T>(endpoint, { method: 'GET', ...options }),
  post: <T>(endpoint: string, data?: any, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    }),
  put: <T>(endpoint: string, data?: any, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    }),
  delete: <T>(endpoint: string, options?: RequestInit) =>
    apiRequest<T>(endpoint, { method: 'DELETE', ...options }),
  patch: <T>(endpoint: string, data?: any, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    }),
};
